import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import axios from 'axios';
import { MapPin, Users, Car, Camera, ArrowRight, Instagram } from 'lucide-react';
import RallyCard from '../components/RallyCard';
import SponsorCard from '../components/SponsorCard';
import Testimonials from '../components/Testimonials';
import InstagramFeed from '../components/InstagramFeed';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [rallies, setRallies] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 150]);
  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ralliesRes, sponsorsRes] = await Promise.all([
          axios.get(`${API_URL}/api/rallies`),
          axios.get(`${API_URL}/api/sponsors?active_only=true`)
        ]);
        setRallies(ralliesRes.data);
        setSponsors(sponsorsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const upcomingRallies = rallies.filter(r => r.status === 'open' || r.status === 'upcoming').slice(0, 2);

  return (
    <div data-testid="home-page" className="bg-[#0A0A0A]">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative h-screen overflow-hidden">
        <motion.div
          style={{ y: heroY }}
          className="absolute inset-0"
        >
          <img
            src="https://i.imgur.com/JbApkxA.jpg"
            alt="Revline Rally"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
        </motion.div>

        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6"
        >
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-['Oswald'] text-5xl sm:text-6xl md:text-8xl font-bold text-[#F2EDE6] text-shadow mb-4"
          >
            THIS IS REVLINE RALLY.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-[#F2EDE6] italic text-shadow mb-2"
          >
            Where experiences hold memories,
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-xl md:text-2xl text-[#F2EDE6] italic text-shadow mb-12"
          >
            and strangers become family.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/rallies"
              data-testid="hero-cta-rallies"
              className="btn-primary flex items-center gap-2"
            >
              View Upcoming Rallies
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/mission"
              data-testid="hero-cta-mission"
              className="btn-secondary"
            >
              Learn Our Story
            </Link>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="absolute bottom-12 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-6 h-10 border-2 border-[#F2EDE6]/50 rounded-full flex justify-center pt-2"
            >
              <motion.div className="w-1 h-2 bg-[#F2EDE6] rounded-full" />
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Marquee */}
      <div className="py-4 bg-[#E8651A]">
        <Marquee speed={50} gradient={false}>
          <span className="font-['Oswald'] text-xl tracking-[0.3em] text-[#0A0A0A] mx-8">
            REVLINE RALLY
          </span>
          <span className="text-[#0A0A0A] mx-4">•</span>
          <span className="font-['Oswald'] text-xl tracking-[0.3em] text-[#0A0A0A] mx-8">
            JOIN THE PACK
          </span>
          <span className="text-[#0A0A0A] mx-4">•</span>
          <span className="font-['Oswald'] text-xl tracking-[0.3em] text-[#0A0A0A] mx-8">
            HOUSTON TX
          </span>
          <span className="text-[#0A0A0A] mx-4">•</span>
          <span className="font-['Oswald'] text-xl tracking-[0.3em] text-[#0A0A0A] mx-8">
            REVLINE RALLY
          </span>
          <span className="text-[#0A0A0A] mx-4">•</span>
          <span className="font-['Oswald'] text-xl tracking-[0.3em] text-[#0A0A0A] mx-8">
            JOIN THE PACK
          </span>
          <span className="text-[#0A0A0A] mx-4">•</span>
        </Marquee>
      </div>

      {/* What We Do Section */}
      <section data-testid="what-we-do-section" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-['Oswald'] text-3xl sm:text-4xl md:text-5xl text-[#F2EDE6] text-center mb-16"
          >
            WHAT WE DO
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: MapPin, title: 'Rally Schedule', desc: 'Curated drives through Texas backroads and beyond.', link: '/rallies' },
              { icon: Car, title: 'Join a Rally', desc: 'Pick your adventure and register your car.', link: '/rallies' },
              { icon: Users, title: 'Sponsors', desc: 'Brands that believe in the rally.', link: '/sponsors' },
              { icon: Camera, title: 'Gallery', desc: 'Moments from the road.', link: '/gallery' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Link
                  to={item.link}
                  data-testid={`what-we-do-${item.title.toLowerCase().replace(' ', '-')}`}
                  className="card block p-8 text-center h-full"
                >
                  <item.icon className="w-10 h-10 text-[#E8651A] mx-auto mb-4" />
                  <h3 className="font-['Oswald'] text-lg text-[#F2EDE6] mb-2">{item.title}</h3>
                  <p className="text-[#A19D98] text-sm">{item.desc}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Teaser */}
      <section data-testid="mission-teaser-section" className="py-24 px-6 bg-[#141414]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-['Oswald'] text-3xl sm:text-4xl md:text-5xl text-[#F2EDE6] mb-8"
          >
            WE DON'T JUST DRIVE CARS. WE LIVE IN THEM.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-[#A19D98] text-lg leading-relaxed mb-8"
          >
            Revline Rally started as car shows in Houston. It evolved into something bigger — 
            curated road trips hitting scenic backroads, hidden food spots, and small-town gems 
            that you'd never find on a regular commute. These aren't just drives. They're 
            gatherings of people who share a passion for cars and life.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <Link
              to="/mission"
              data-testid="mission-teaser-cta"
              className="btn-secondary inline-flex items-center gap-2"
            >
              Read Our Full Story
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Rallies Preview */}
      {upcomingRallies.length > 0 && (
        <section data-testid="upcoming-rallies-section" className="py-24 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex justify-between items-end mb-12"
            >
              <h2 className="font-['Oswald'] text-3xl sm:text-4xl md:text-5xl text-[#F2EDE6]">
                UPCOMING RALLIES
              </h2>
              <Link
                to="/rallies"
                data-testid="see-all-rallies"
                className="text-[#E8651A] hover:text-[#FF7A2F] flex items-center gap-2 font-['Oswald'] tracking-wider"
              >
                See All
                <ArrowRight size={16} />
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {upcomingRallies.map((rally, i) => (
                <RallyCard key={rally.id} rally={rally} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sponsors Strip */}
      <section data-testid="sponsors-section" className="py-24 px-6 bg-[#141414]">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-['Oswald'] text-3xl sm:text-4xl md:text-5xl text-[#F2EDE6] text-center mb-16"
          >
            OUR RALLY FAMILY
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {sponsors.slice(0, 3).map((sponsor, i) => (
              <SponsorCard key={sponsor.id} sponsor={sponsor} index={i} />
            ))}
          </div>

          {/* Placeholder slots */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
            {[1, 2, 3].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="border-2 border-dashed border-[#2A2A2A] p-8 flex flex-col items-center justify-center min-h-[200px]"
              >
                <span className="text-[#A19D98] text-sm font-['Oswald'] tracking-wider">
                  YOUR BRAND HERE
                </span>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/sponsors#contact"
              data-testid="become-sponsor-cta"
              className="btn-secondary inline-flex items-center gap-2"
            >
              Become a Sponsor
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Instagram Feed */}
      <InstagramFeed />

      {/* Instagram Section */}
      <section data-testid="instagram-section" className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Instagram className="w-12 h-12 text-[#E8651A] mx-auto mb-6" />
            <h2 className="font-['Oswald'] text-3xl sm:text-4xl text-[#F2EDE6] mb-4">
              FOLLOW THE JOURNEY
            </h2>
            <p className="text-[#A19D98] mb-8">
              Join 1,400+ car enthusiasts on Instagram
            </p>
            <a
              href="https://www.instagram.com/revlinerally/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-btn"
              className="btn-primary inline-flex items-center gap-2"
            >
              @revlinerally
              <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
